import DVIRLog, { IDVIRLog } from "./DVIRLog";
import { extend, hashToArray } from "../utils/vanilla";
import { optionalDateTimeComparator } from "../utils/date";
import { DriveStore } from "../store/reducers";
import { UserService } from "../utils/users";

export class DVIRLogsService {

    constructor(private readonly store: DriveStore, private readonly userService: UserService) {
    }

    /**
     * Method to get dvirlogs as an array.
     * @param overrideUser Optional parameter for which user to get DVIRLogs for. Default is the current user.
     * @returns array of dvir logs in the store.
     */
    getDvirLogs(overrideUser?: string) {
        return hashToArray(this.getDvirLogsAsDictionary(overrideUser)).sort(optionalDateTimeComparator());
    }

    /**
     * Method to get dvirlogs as a dictionary. The logs are cloned.
     * @param overrideUser Optional parameter for which user to get DVIRLogs for. Default is the current user.
     * @returns dictionary of dvir logs in the store.
     */
    getDvirLogsAsDictionary(overrideUser?: string) {
        const userId = this.getUserId(overrideUser);
        if (!userId) {
            return {};
        }
        let logs: DeepReadonlyObject<IIdDictionary<IDVIRLog>> = this.store.getState().dvirLogs[userId] || {};
        let logsDictionary: IIdDictionary<DVIRLog> = {};
        Object.keys(logs).forEach(key => {
            if (logs) {
                logsDictionary[key] = new DVIRLog(extend(true, {}, logs[key]) as IDVIRLog);
            }
        });

        return logsDictionary;
    }

    /**
     * Method to reset the properties of a DVIRLog to match what is currently in the store.
     * @param dvirToUpdate The DVIRLog object to update.
     * @param overrideUser Optional parameter for which user to get DVIRLogs for. Default is the current user.
     */
    resetProperties(dvirToUpdate: DVIRLog, overrideUser?: string) {
        let logs = this.getDvirLogsAsDictionary(overrideUser);
        let logFromStorage = logs[dvirToUpdate.id];

        if (logFromStorage) {
            for (let property of Object.keys(dvirToUpdate) as (keyof DVIRLog)[]) {
                (dvirToUpdate as any)[property] = logFromStorage[property];
            }
        }
    }

    /**
     * Method to get the DVIRLog by ID
     * @param dvirId
     * @param overrideUser Optional parameter for which user to get DVIRLogs for. Default is the current user.
     * @returns log matching the specified id, otherwise null.
     */
    getDvirLogById(dvirId: string, overrideUser?: string) {
        const dvirLogs = this.getDvirLogsAsDictionary(overrideUser);
        return dvirLogs[dvirId] || null;
    }

    /**
     * Method to get the most recent DVIRLog for the specified asset.
     * @param dvirLogs
     * @param resourceId
     * @param excludeInTrip
     * @returns log matching the specified parameters, otherwise null.
     */
    getLatestLog(dvirLogs: DVIRLog[], resourceId: string, excludeInTrip = false) {
        // Sort DVIR logs in ascending order considering dateTime
        let dvirArray = dvirLogs.sort(optionalDateTimeComparator());

        //If no resource specified, return the last record
        if (!resourceId) {
            return dvirArray[dvirArray.length - 1];
        }

        for (let i = dvirArray.length - 1; i >= 0; i--) {
            let log = dvirArray[i];

            if (log.getResource().id === resourceId && (!excludeInTrip || !log.isInTrip())) {
                return log;
            }
        }

        return null;
    }

    /**
     * Method to get the latest required log. Required log means the oldest Pre-Trip/Post-Trip log that is accepted.
     * @param dvirLogs
     * @param resourceId
     * @returns log matching the specified parameters, otherwise null.
     */
    getLatestRequiredLog(dvirLogs: DVIRLog[], resourceId?: string) {
        let latestLog: DVIRLog | null = null;

        let dvirArray = dvirLogs.sort(optionalDateTimeComparator(false));

        for (let i = 0; i < dvirArray.length; i++) {
            if (dvirArray[i].getResource().id === resourceId) {
                // We only want to keep one accepted in trip log, we only stack them if there is at least one pre/post trip
                if (!latestLog || !(latestLog.isInTrip() && dvirArray[i].isInTrip())) {
                    latestLog = dvirArray[i];
                }

                if (!dvirArray[i].isInTrip()) {
                    break;
                }
            }
        }
        return latestLog;
    }

    /**
     * Method to get all DVIRLogs after the specified fromDate (if applicable).
     * @param dvirLogs
     * @param resourceId
     * @param fromDate
     * @returns an array of logs matching the specified parameters.
     */
    getDvirsAfterDate(dvirLogs: DVIRLog[], resourceId: string, fromDate: string | null = null) {
        return dvirLogs.filter(dvirLog => dvirLog.getResource().id === resourceId && (!fromDate || dvirLog.isDvirLogAfterDate(fromDate)));
    }

    /**
     * Method to get all DVIRLogs after the specified fromDate (if applicable) that are type In-Trip.
     * @param dvirLogs
     * @param resourceId
     * @param fromDate
     * @returns an array of logs matching the specified parameters.
     */
    getInTripDvirsForAsset(dvirLogs: DVIRLog[], resourceId: string, fromDate: string | null = null) {
        return dvirLogs.filter(dvirLog => dvirLog.getResource().id === resourceId && dvirLog.isInTrip() && (!fromDate || dvirLog.isDvirLogAfterDate(fromDate)));
    }
    private getUserId(overrideUser: string | undefined) {
        return overrideUser ?? this.userService.getCurrentUser()?.id;
    }
}